import React, { useCallback, useState } from 'react';
import {
  FlexView,
  DatePicker,
  TimePicker,
  SelectDropdown,
  RequiredFieldIndicator,
} from '@gsa/afp-component-library';
import { useContractLine } from '../provider/contract-line-provider';
import { ReadOnlyWrapper } from '../helpers/ec-components';
import {
  getETString,
  getUTCString,
  formatDatePickerValue,
} from '../../components/contract-helpers';

const StartEndDateTimePicker = () => {
  const { contractLine, lineTabData, setLineTabData, canUpdateContract } =
    useContractLine();

  const startDate = getETString(
    'orderStartDate' in lineTabData
      ? lineTabData.orderStartDate
      : contractLine?.orderStartDate,
  );
  const [formattedStartDate, formattedStartTime] = startDate?.split(' ') || [
    null,
    null,
  ];

  const endDate = getETString(
    'orderEndDate' in lineTabData
      ? lineTabData?.orderEndDate
      : contractLine?.orderEndDate,
  );
  const [formattedEndDate, formattedEndTime] = endDate?.split(' ') || [
    null,
    null,
  ];

  const isDirect = contractLine?.isDirect;

  // bellow is used to work around a bug in the DatePicker component, which calls on change on intial render
  const [firstDateComChange, setFirstDateComChange] = useState({
    orderStartDate: !!formattedStartDate,
    orderStartDateTime: true,
    orderEndDate: !!formattedEndDate,
    orderEndDateTime: true,
  });

  // variables to hold the time picker values
  const [timeValues, setTimeValues] = useState({
    orderStartDate: formattedStartTime,
    orderEndDate: formattedEndTime,
  });

  const onDateChange = useCallback(
    (fieldKey, value) => {
      const isFirstInitialOnChange = firstDateComChange[fieldKey];
      if (isFirstInitialOnChange) {
        setFirstDateComChange((prev) => ({
          ...prev,
          [fieldKey]: false,
        }));
        return;
      }

      const newDateStr = value
        ? `${formatDatePickerValue(value)} ${timeValues[fieldKey] || '00:00'}`
        : null;
      setLineTabData((prev) => ({
        ...prev,
        [fieldKey]: getUTCString(newDateStr),
      }));
    },
    [firstDateComChange, timeValues],
  );

  const onTimeChange = useCallback(
    (fieldKey, value) => {
      const isFirstInitialOnChange = firstDateComChange[`${fieldKey}Time`];
      if (isFirstInitialOnChange) {
        setFirstDateComChange((prev) => ({
          ...prev,
          [`${fieldKey}Time`]: false,
        }));
        return;
      }

      setTimeValues((prev) => ({ ...prev, [fieldKey]: value }));

      const dateStr = fieldKey === 'orderStartDate' ? startDate : endDate;
      const newDateStr = dateStr
        ? `${dateStr.substring(0, 10)} ${value || '00:00'}`
        : null;
      setLineTabData((prev) => ({
        ...prev,
        [fieldKey]: getUTCString(newDateStr),
      }));
    },
    [firstDateComChange, startDate, endDate],
  );

  const onDirectChange = (value) => {
    setLineTabData((prev) => ({ ...prev, isDirect: value }));
  };

  return (
    <>
      <FlexView column className="margin-top-6">
        <div className="grid-row margin-bottom-6">
          <ReadOnlyWrapper wrapperType="grid" dataType="date">
            <DatePicker
              id="open-date"
              name="openDate"
              maxDate={formattedEndDate || undefined}
              defaultValue={formattedStartDate}
              className="margin-right-4"
              labelClass="text-bold margin-bottom-2"
              label="Open date"
              onChange={(value) => onDateChange('orderStartDate', value)}
              disabled={!canUpdateContract}
            />
          </ReadOnlyWrapper>
          <ReadOnlyWrapper wrapperType="grid" dataType="time">
            <TimePicker
              id="open-time"
              defaultValue={formattedStartTime}
              name="openTime"
              label="Open time (ET)"
              className="margin-right-4"
              onChange={(value) => onTimeChange('orderStartDate', value)}
              disabled={!canUpdateContract}
            />
          </ReadOnlyWrapper>
          <ReadOnlyWrapper wrapperType="grid" dataType="date">
            <DatePicker
              id="close-date"
              name="closeDate"
              defaultValue={formattedEndDate}
              minDate={formattedStartDate}
              labelClass="text-bold margin-bottom-2"
              className="margin-right-4"
              label="Closeout date"
              onChange={(value) => onDateChange('orderEndDate', value)}
              disabled={!canUpdateContract}
            />
          </ReadOnlyWrapper>
          <ReadOnlyWrapper wrapperType="grid" dataType="time">
            <TimePicker
              id="close-time"
              name="closeTime"
              defaultValue={formattedEndTime}
              label="Closeout time (ET)"
              onChange={(value) => onTimeChange('orderEndDate', value)}
              disabled={!canUpdateContract}
            />
          </ReadOnlyWrapper>
        </div>
        <div className="width-card">
          <div className="text-bold">
            Direct <RequiredFieldIndicator />
          </div>
          <ReadOnlyWrapper wrapperType="grid" dataType="boolean">
            <SelectDropdown
              id="isDirect"
              name="isDirect"
              options={[
                { value: '', label: '-Select-' },
                { value: 1, label: 'Yes' },
                { value: 0, label: 'No' },
              ]}
              value={isDirect}
              onChange={(e) => {
                onDirectChange(e.target.value);
              }} // Handle state change
              required
              disabled={!canUpdateContract}
            />
          </ReadOnlyWrapper>
        </div>
      </FlexView>
    </>
  );
};

export default StartEndDateTimePicker;
